import { useCallback, useEffect, useRef, useState } from 'react';
import cancelDeliveryDocumentsDownload from '../../../../repository/delivery-documents/raw-milk/cancelDeliveryDocumentsDownload';
import createDeliveryDocumentsDownload from '../../../../repository/delivery-documents/raw-milk/createDeliveryDocumentsDownload';
import watchDeliveryDocumentsDownload from '../../../../repository/delivery-documents/raw-milk/watchDeliveryDocumentsDownload';
import downloadInFrame from './downloadInFrame';
import { getExpressAPIHost } from '../../../../../utils';

const IDLE = { status: 'idle' };

const useDeliveryReportsDownload = () => {
    const [state, setState] = useState(IDLE);
    const run = useRef(null);

    const stop = useCallback(({ cancelOnServer }) => {
        const { current } = run;
        if (!current) return;
        run.current = null;
        current.controller.abort();
        current.removeFrame?.();
        if (cancelOnServer && current.token) cancelDeliveryDocumentsDownload(current.token);
    }, []);

    const start = useCallback(
        async (ids, layout = [], { cancelPrevious = true } = {}) => {
            stop({ cancelOnServer: cancelPrevious });
            const current = { controller: new AbortController() };
            run.current = current;
            const { signal } = current.controller;
            setState({ status: 'running', ids, layout, stage: 'starting', completed: 0, total: ids.length, startedAt: Date.now() });
            try {
                const ticket = await createDeliveryDocumentsDownload(ids, layout, { signal });
                if (signal.aborted) {
                    cancelDeliveryDocumentsDownload(ticket.token);
                    return;
                }
                current.token = ticket.token;
                current.removeFrame = downloadInFrame(`${getExpressAPIHost()}/delivery-documents/zip/${ticket.token}`);
                setState((previous) => ({ ...previous, total: ticket.total, filename: ticket.filename }));
                const result = await watchDeliveryDocumentsDownload(ticket.token, {
                    signal,
                    onProgress: (event) => {
                        if (signal.aborted) return;
                        setState((previous) => (previous.status === 'running' ? { ...previous, stage: event.stage, completed: event.completed, total: event.total } : previous));
                    },
                });
                if (signal.aborted) return;
                setState({ status: 'done', ids, layout, ...result });
            } catch (err) {
                if (signal.aborted || err.name === 'AbortError') return;
                setState({ status: err.lostTrack ? 'lost' : 'failed', ids, layout, error: err.message });
            }
        },
        [stop]
    );

    const retry = useCallback(() => {
        if (state.ids) start(state.ids, state.layout, { cancelPrevious: state.status !== 'lost' });
    }, [state.ids, state.layout, state.status, start]);

    const cancel = useCallback(() => {
        stop({ cancelOnServer: true });
        setState(IDLE);
    }, [stop]);

    const close = useCallback(() => {
        stop({ cancelOnServer: false });
        setState(IDLE);
    }, [stop]);

    const downloading = state.status === 'running';

    useEffect(() => {
        if (!downloading) return undefined;
        const warn = (event) => {
            event.preventDefault();
            Object.assign(event, { returnValue: '' });
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [downloading]);

    useEffect(() => () => stop({ cancelOnServer: false }), [stop]);

    return { state, downloading, start, retry, cancel, close };
};

export default useDeliveryReportsDownload;
